import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatScreenProps {
  token: string;
  onLogout: () => void;
}

const ChatScreen: React.FC<ChatScreenProps> = ({ token, onLogout }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (type: 'user' | 'assistant', content: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      type,
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/copilot/refresh`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        addMessage('assistant', 'Workspace data refreshed successfully! You can now ask questions about your latest data.');
      }
    } catch (error: any) {
      console.error('Refresh failed:', error);
      if (error.response?.status === 401) {
        addMessage('assistant', 'Your session has expired. Please log in again.');
        setTimeout(() => onLogout(), 2000);
      } else {
        addMessage('assistant', 'Failed to refresh workspace data. Please try again.');
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setError(null);
    
    addMessage('user', userMessage);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/copilot/query`,
        {
          prompt: userMessage,
          limit: 10
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { answer, sources } = response.data;
      
      let responseText = answer;
      
      if (sources && sources.length > 0) {
        responseText += "\n\nSources:";
        sources.forEach((source: any, index: number) => {
          responseText += `\n${index + 1}. ${source.metadata.type}: ${source.content}`;
        });
      }

      addMessage('assistant', responseText);
    } catch (error: any) {
      console.error('Query failed:', error);
      let errorMessage = 'Failed to process your request.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Your session has expired. Please log in again.';
        // Auto-logout on 401
        setTimeout(() => onLogout(), 2000);
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      addMessage('assistant', `Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h1 className="chat-title">Nuerologos</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="refresh-button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4285f4',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
              opacity: isRefreshing ? 0.6 : 1
            }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
          <button className="logout-button" onClick={onLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {error && <div className="error">{error}</div>}
        
        {messages.length === 0 && (
          <div className="message assistant">
            <div className="message-content">
              Hello! I'm your copilot assistant. I can help you with:
              <br />• Questions about your emails, calendar, and files
              <br />• Finding specific information across your workspace
              <br />• Summarizing and analyzing your data
              <br />• Natural conversation about your work
              <br /><br />
              Try asking: "What meetings do I have this week?" or "Summarize my recent emails"
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-content">
              {message.content.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < message.content.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="message assistant">
            <div className="message-content loading">
              Processing your request...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <form className="chat-input-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="chat-input"
            placeholder="Ask about your emails, calendar, or files..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={isLoading}
          />
          <button
            type="submit"
            className="send-button"
            disabled={!inputValue.trim() || isLoading}
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatScreen;
